import { useEffect, useState, createContext, useContext, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import { SiteProvider } from "@/website/SiteLayout";
import { HomeGate, PublicPage, BookTrialPage, SitePreviewPage } from "@/website/PublicPage";
import LoginPage from "@/pages/LoginPage";
import StudentDashboard from "@/pages/StudentDashboard";
import TeacherDashboard from "@/pages/TeacherDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import ClassDetail from "@/pages/ClassDetail";
import QuizBuilder from "@/pages/QuizBuilder";
import QuizEditor from "@/pages/QuizEditor";
import QuizTake from "@/pages/QuizTake";
import StudentAnalytics from "@/pages/StudentAnalytics";
import QuizHistory from "@/pages/QuizHistory";
import Leaderboard from "@/pages/Leaderboard";
import AIChatPage from "@/pages/AIChatPage";
import WorksheetGenerator from "@/pages/WorksheetGenerator";
import MathsGenerator from "@/pages/MathsGenerator";
import ArenaPage from "@/pages/ArenaPage";
import GuestArenaPage from "@/pages/GuestArenaPage";
import SecureExamsHub from "@/pages/SecureExamsHub";
import SecureExamBuilder from "@/pages/SecureExamBuilder";
import SecureExamRunner from "@/pages/SecureExamRunner";
import SecureExamMonitor from "@/pages/SecureExamMonitor";
import SecureExamResult from "@/pages/SecureExamResult";
import AuthCallback from "@/pages/AuthCallback";
import MediaLibrary from "@/pages/MediaLibrary";
import SharedSolution from "@/pages/SharedSolution";
import BrainGymPage from "@/pages/BrainGymPage";
import MathsHubPage from "@/pages/MathsHubPage";
import BrainGamePlay from "@/pages/BrainGamePlay";
import AbacusPage from "@/pages/AbacusPage";
import VisualizationPage from "@/pages/VisualizationPage";
import CoursesPage from "@/pages/CoursesPage";
import LessonPage from "@/pages/LessonPage";
import { SiteContentProvider } from "@/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem("token"));

  const checkAuth = useCallback(async () => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      // Ignore stale responses: a newer login may have replaced the token mid-flight
      if (localStorage.getItem("token") !== token) return;
      setUser(null);
      setToken(null);
      localStorage.removeItem("token");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // If returning from Google OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = (userData, accessToken) => {
    setUser(userData);
    if (accessToken) {
      setToken(accessToken);
      localStorage.setItem("token", accessToken);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, token, login, logout, updateUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect based on role
    const redirectPath = user.role === "admin" ? "/admin" : user.role === "teacher" ? "/teacher" : user.role === "player" ? "/play/arena" : "/student";
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();
  // Detect Google OAuth return synchronously during render (before ProtectedRoute runs).
  // /play/* pages handle their own session_id exchange (Arena Google sign-in).
  if (location.hash?.includes("session_id=") && !location.pathname.startsWith("/play/")) {
    return <AuthCallback />;
  }
  return (
    <SiteProvider>
    <Routes>
      <Route path="/" element={<HomeGate />} />
      <Route path="/book-trial" element={<BookTrialPage />} />
      <Route path="/site-preview/:pageId" element={<ProtectedRoute allowedRoles={["admin"]}><SitePreviewPage /></ProtectedRoute>} />
      <Route path="/s/:shareId" element={<SharedSolution />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/student" element={<LoginPage />} />
      <Route path="/login/teacher" element={<LoginPage />} />
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/student" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <StudentDashboard />
        </ProtectedRoute>
      } />
      <Route path="/teacher" element={
        <ProtectedRoute allowedRoles={["teacher"]}>
          <TeacherDashboard />
        </ProtectedRoute>
      } />
      <Route path="/class/:classId" element={
        <ProtectedRoute>
          <ClassDetail />
        </ProtectedRoute>
      } />
      <Route path="/quiz/create/:classId" element={
        <ProtectedRoute allowedRoles={["teacher"]}>
          <QuizBuilder />
        </ProtectedRoute>
      } />
      <Route path="/quiz/edit/:quizId" element={
        <ProtectedRoute allowedRoles={["teacher"]}>
          <QuizEditor />
        </ProtectedRoute>
      } />
      <Route path="/quiz/:quizId" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <QuizTake />
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <StudentAnalytics />
        </ProtectedRoute>
      } />
      <Route path="/quiz-history" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <QuizHistory />
        </ProtectedRoute>
      } />
      <Route path="/leaderboard" element={
        <ProtectedRoute>
          <Leaderboard />
        </ProtectedRoute>
      } />
      <Route path="/leaderboard/:classId" element={
        <ProtectedRoute>
          <Leaderboard />
        </ProtectedRoute>
      } />
      <Route path="/ai-tutor" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <AIChatPage />
        </ProtectedRoute>
      } />
      <Route path="/brain-gym" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <BrainGymPage />
        </ProtectedRoute>
      } />
      <Route path="/mental-maths" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <MathsHubPage hub="mental" />
        </ProtectedRoute>
      } />
      <Route path="/vedic-maths" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <MathsHubPage hub="vedic" />
        </ProtectedRoute>
      } />
      <Route path="/brain-gym/:mode" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <BrainGamePlay />
        </ProtectedRoute>
      } />
      <Route path="/abacus" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <AbacusPage />
        </ProtectedRoute>
      } />
      <Route path="/visualize" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <VisualizationPage />
        </ProtectedRoute>
      } />
      <Route path="/courses" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <CoursesPage />
        </ProtectedRoute>
      } />
      <Route path="/courses/:courseId/:lessonId" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <LessonPage />
        </ProtectedRoute>
      } />
      <Route path="/worksheet-generator" element={<WorksheetGenerator />} />
      <Route path="/maths-generator" element={<MathsGenerator />} />
      <Route path="/arena" element={<ArenaPage />} />
      <Route path="/play/arena" element={<GuestArenaPage />} />
      <Route path="/play/times-tables" element={<GuestArenaPage />} />
      <Route path="/secure-exams" element={
        <ProtectedRoute>
          <SecureExamsHub />
        </ProtectedRoute>
      } />
      <Route path="/media-library" element={
        <ProtectedRoute allowedRoles={["teacher", "admin"]}>
          <MediaLibrary />
        </ProtectedRoute>
      } />
      <Route path="/secure-exams/new" element={
        <ProtectedRoute allowedRoles={["teacher", "admin"]}>
          <SecureExamBuilder />
        </ProtectedRoute>
      } />
      <Route path="/secure-exams/:examId/edit" element={
        <ProtectedRoute allowedRoles={["teacher", "admin"]}>
          <SecureExamBuilder />
        </ProtectedRoute>
      } />
      <Route path="/secure-exams/:examId/monitor" element={
        <ProtectedRoute allowedRoles={["teacher", "admin"]}>
          <SecureExamMonitor />
        </ProtectedRoute>
      } />
      <Route path="/secure-exams/:examId/results" element={
        <ProtectedRoute allowedRoles={["teacher", "admin"]}>
          <SecureExamResult />
        </ProtectedRoute>
      } />
      <Route path="/secure-exam/:examId/take" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <SecureExamRunner />
        </ProtectedRoute>
      } />
      <Route path="/secure-exam/:examId/result" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <SecureExamResult />
        </ProtectedRoute>
      } />
      <Route path="*" element={<PublicPage />} />
    </Routes>
    </SiteProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SiteContentProvider>
          <AppRouter />
        </SiteContentProvider>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
